import { useEffect, useRef, useState } from "react";
import QuillReal from "quill";
import QuillImageDropAndPaste from "quill-image-drop-and-paste";
import "quill/dist/quill.snow.css";
import QuillResizeImage from "../../../../helpers/quillResizeImageWithAlt";
import { httpRequest } from "../../../../helpers/api";
import { message, Spin } from "antd";
import { Quill } from "react-quill";
import { imageResizeWithQuality, isImageMoreThanXMB } from "../../../../helpers/image";
const Image = Quill.import("formats/image");

class ImageBlot extends Image {
	static create(value: any) {
		const node = super.create(value);
		if (typeof value === "string") {
			node.setAttribute("src", this.sanitize(value));
			node.setAttribute("alt", this.sanitize(value).split("/").reverse()[0]);
		}
		return node;
	}
}
interface IProps {
	value?: string;
	articleId: string;
	onChange: (value: any) => void;
}

export default function TextEditorBlog({ articleId, onChange, value }: IProps) {
	const quillRef = useRef<QuillReal | null>(null);
	const [isUploading, setIsUploading] = useState(false);

	const quillFormats = [
		"header",
		"size",
		"bold",
		"italic",
		"underline",
		"list",
		"bullet",
		"strike",
		"link",
		"image",
		"video",
		"color",
		"font",
		"background",
		"align",
		"blockquote",
		"code-block",
	];

	const quillModulesArticleBody = {
		toolbar: [
			[{ header: [1, 2, 3, 4, 5, 6, false] }],
			[{ size: ["small", false, "large", "huge"] }],
			["blockquote", "code-block"],
			["bold", "italic", "underline", "strike"],
			["link", "image", "video"],
			[{ color: [] }, { background: [] }],
			[{ font: [] }],
			[{ align: [] }],
			[{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
			["clean"],
		],
		imageDropAndPaste: {
			handler: imageHandler,
		},
		resize: {
			locale: {},
		},
	};

	async function imageHandler(imageDataUrl: string, type: string, imageData: any) {
		setIsUploading(true);
		imageData
			.minify({
				maxWidth: 320,
				maxHeight: 320,
				quality: 0.7,
			})
			.then(async (miniImageData: any) => {
				let file = miniImageData.toFile();

				if (isImageMoreThanXMB(file, 2)) {
					file = await imageResizeWithQuality(file, 2);
				}

				// generate a form data
				const formData = new FormData();

				// or just append the file
				formData.append("image", file as File);

				httpRequest
					.post("/articles/image-blog/" + articleId + `/${undefined}`, formData)
					.then((resImage: any) => {
						if (quillRef.current) {
							let index = (quillRef.current.getSelection() || {}).index;
							if (index === undefined || index < 0) index = quillRef.current.getLength();
							quillRef.current.insertEmbed(
								index,
								"image",
								resImage.data.payload.fileLinkCache,
								resImage?.data?.payload?.tableName
							);
						}
						message.success("Image uploaded successfully!");
					})
					.catch((err) => {
						message.error("Error upload image");
					})
					.finally(() => {
						setIsUploading(false);
					});
			})
			.catch((err: any) => {
				message.error("Error processing image");
				setIsUploading(false);
			});
	}

	useEffect(() => {
		Quill.register("modules/imageDropAndPaste", QuillImageDropAndPaste);
		Quill.register("modules/resize", QuillResizeImage);
		Quill.register(ImageBlot);
		const quill = new Quill("#editor-container", {
			modules: quillModulesArticleBody,
			theme: "snow",
			formats: quillFormats,
		});
		quillRef.current = quill;
	}, []);

	useEffect(() => {
		if (quillRef.current) {
			// Add custom handler for Image Upload
			quillRef.current.getModule("toolbar").addHandler("image", selectLocalImage);
		}
	}, [quillRef]);

	// Open Dialog to select Image File
	const selectLocalImage = () => {
		const input = document.createElement("input");
		input.setAttribute("type", "file");
		input.setAttribute("accept", "image/*");
		input.click();

		input.onchange = () => {
			const file = input?.files![0];
			saveToServer(file);
		};
	};

	const saveToServer = async (file: any) => {
		if (file) {
			setIsUploading(true);
			const compressedFile = await imageResizeWithQuality(file, 2);
			// generate a form data
			const formData = new FormData();

			// or just append the file
			formData.append("image", compressedFile as File);

			httpRequest
				.post("/articles/image-blog/" + articleId + `/${undefined}`, formData)
				.then((resImage: any) => {
					console.log("Success upload to server");
					if (quillRef.current) {
						let index = (quillRef.current.getSelection() || {}).index;
						if (index === undefined || index < 0) index = quillRef.current.getLength();
						quillRef.current.insertEmbed(
							index,
							"image",
							resImage.data.payload.fileLinkCache,
							resImage?.data?.payload?.tableName
						);
					}
					message.success("Image uploaded successfully!");
				})
				.catch((err: any) => {
					message.error("Error upload image");
				})
				.finally(() => {
					setIsUploading(false);
				});
		} else {
			message.error("File not found!");
			setIsUploading(false);
		}
	};

	// console.log(quillRef.current);
	useEffect(() => {
		if (quillRef.current) {
			quillRef.current.on("text-change", () => {
				const content = quillRef?.current?.root.innerHTML; // Or getText() for plain text
				onChange?.(content);
			});
		}
	}, [quillRef?.current]);

	useEffect(() => {
		if (quillRef.current && value !== quillRef.current.root.innerHTML) {
			quillRef.current.root.innerHTML = value || "";
		}
	}, [value]);

	return (
		<Spin spinning={isUploading} tip="Uploading image...">
			<div
				id="editor-container"
				className="ql2"
				style={{ minHeight: "320px" }}
			></div>
		</Spin>
	);
}
